import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Facebook, Instagram, ArrowRight, Star, Shield, DollarSign, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    zipCode: '',
    textUpdates: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/volunteer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone || null,
          zip_code: formData.zipCode || null,
          interests: [],
          text_updates: formData.textUpdates
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Thank you for signing up. Our team will be in touch shortly.');
        setFormData({ firstName: '', lastName: '', email: '', phone: '', zipCode: '', textUpdates: false });
      } else {
        toast.error(data.detail || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const priorities = [
    {
      icon: Star,
      title: "School Choice",
      description: "Empowering parents with educational options for their children"
    },
    {
      icon: Shield,
      title: "Public Safety",
      description: "Supporting our law enforcement and keeping communities safe"
    },
    {
      icon: DollarSign,
      title: "Affordability",
      description: "Fighting for lower taxes and economic opportunity for families"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Face-First, Clean Layout */}
      <section className="relative h-[850px] sm:h-[720px] lg:h-[850px] overflow-hidden">
        {/* Background Image - Positioned to show face clearly */}
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_f99a18a4-e930-4176-ac10-f2e206aadd3b/artifacts/els7t457_2955357918537146080.jpeg"
            alt="Kalinda Washington"
            className="w-full h-full object-cover brightness-110 contrast-105"
            style={{ objectPosition: '70% 10%' }}
          />
          {/* Mobile: Bottom gradient only to keep face clear at top */}
          {/* Desktop: Left-side gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628]/95 via-[#0a1628]/40 via-50% to-transparent sm:bg-gradient-to-r sm:from-[#0a1628]/90 sm:via-[#0a1628]/60 sm:via-40% sm:to-transparent"></div>
          <div className="absolute inset-0 hidden sm:block bg-gradient-to-t from-[#0a1628]/50 via-transparent via-60% to-transparent"></div>
        </div>

        {/* Hero Content - Mobile: Bottom aligned, Desktop: Center left */}
        <div className="relative z-10 h-full flex items-end sm:items-center pb-12 sm:pb-0">
          <div className="container mx-auto px-5 sm:px-6 lg:px-10">
            <div className="max-w-full sm:max-w-xl lg:max-w-2xl">
              {/* Campaign Badge */}
              <div className="inline-flex items-center bg-white shadow-lg rounded-full px-3 sm:px-4 py-1.5 sm:py-2 mb-4 sm:mb-8">
                <span className="text-red-600 font-bold text-[9px] sm:text-xs tracking-[0.12em] sm:tracking-[0.15em] uppercase">Republican for NY State Assembly</span>
              </div>
              
              {/* Large Headline */}
              <h1 className="text-4xl sm:text-6xl lg:text-7xl xl:text-8xl font-black text-white mb-4 sm:mb-6 leading-[0.95] tracking-tight drop-shadow-2xl">
                Kalinda<br />Washington
              </h1>
              
              {/* Accent Line */}
              <div className="w-20 sm:w-24 h-1.5 sm:h-2 bg-red-600 mb-5 sm:mb-8 rounded-full shadow-lg"></div>
              
              {/* Description */}
              <p className="text-base sm:text-xl lg:text-2xl text-white font-medium mb-6 sm:mb-10 leading-relaxed max-w-sm sm:max-w-md drop-shadow-lg">
                Fighting for school choice, safe communities, and affordability for Monroe County families.
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-5 items-stretch sm:items-start">
                {/* Primary CTA */}
                <a href="https://buy.stripe.com/4gMeVec2v4oP6XP3sQ6g803" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
                  <Button 
                    size="lg" 
                    className="bg-red-600 hover:bg-red-700 text-white font-black px-8 sm:px-10 py-5 sm:py-7 text-sm sm:text-base lg:text-lg uppercase tracking-widest shadow-[0_8px_30px_rgba(220,38,38,0.5)] hover:shadow-[0_12px_40px_rgba(220,38,38,0.6)] hover:scale-105 transition-all duration-300 w-full sm:w-auto rounded-lg"
                  >
                    Contribute Today
                  </Button>
                </a>
                
                {/* Secondary CTA */}
                <Link to="/meet-kalinda" className="w-full sm:w-auto">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="bg-white/95 hover:bg-white text-[#0a1628] font-bold px-8 sm:px-10 py-5 sm:py-7 text-sm sm:text-base lg:text-lg border-[3px] border-white shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 w-full sm:w-auto rounded-lg"
                  >
                    Meet Kalinda
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Accent Bar */}
        <div className="absolute bottom-0 left-0 right-0 h-1.5 sm:h-2 bg-gradient-to-r from-red-600 via-white to-[#0a1628]"></div>
      </section>

      {/* Priorities Section */}
      <section className="py-16 bg-[#0a1628]">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {priorities.map((priority, index) => (
              <div 
                key={index} 
                className="text-center p-6 border border-white/10 rounded-lg hover:border-red-600/50 transition-colors duration-300"
              >
                <div className="w-14 h-14 bg-red-600/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <priority.icon className="w-7 h-7 text-red-500" />
                </div>
                <h3 className="text-white font-bold text-lg mb-2">{priority.title}</h3>
                <p className="text-white/70 text-sm leading-relaxed">{priority.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Join Our Team Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl lg:text-4xl font-bold text-[#0a1628] mb-3">
                Join Our Team
              </h2>
              <p className="text-gray-600 text-lg">
                Be part of the movement to bring common-sense leadership to Albany.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8 lg:p-10 border border-gray-100">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      First Name <span className="text-red-600">*</span>
                    </label>
                    <Input
                      type="text"
                      name="firstName"
                      required
                      value={formData.firstName}
                      onChange={handleChange}
                      className="w-full h-11 text-base border-gray-200 focus:border-[#0a1628] focus:ring-[#0a1628] rounded-lg"
                      placeholder="Enter your first name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Last Name <span className="text-red-600">*</span>
                    </label>
                    <Input
                      type="text"
                      name="lastName"
                      required
                      value={formData.lastName}
                      onChange={handleChange}
                      className="w-full h-11 text-base border-gray-200 focus:border-[#0a1628] focus:ring-[#0a1628] rounded-lg"
                      placeholder="Enter your last name"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Email <span className="text-red-600">*</span>
                    </label>
                    <Input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full h-11 text-base border-gray-200 focus:border-[#0a1628] focus:ring-[#0a1628] rounded-lg"
                      placeholder="you@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Phone <span className="text-gray-400">(Optional)</span>
                    </label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full h-11 text-base border-gray-200 focus:border-[#0a1628] focus:ring-[#0a1628] rounded-lg"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Zip Code <span className="text-gray-400">(Optional)</span>
                  </label>
                  <Input
                    type="text"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleChange}
                    className="w-full h-11 text-base border-gray-200 focus:border-[#0a1628] focus:ring-[#0a1628] rounded-lg md:w-1/2"
                    placeholder="14614"
                    maxLength={10}
                  />
                </div>

                <div className="flex items-start space-x-3 pt-2">
                  <Checkbox
                    id="textUpdates"
                    checked={formData.textUpdates}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, textUpdates: checked }))}
                    className="mt-0.5 border-gray-300"
                  />
                  <label htmlFor="textUpdates" className="text-sm text-gray-600 leading-relaxed">
                    Yes, I would like to receive text message updates. Msg & data rates may apply. Reply STOP to opt-out.
                  </label>
                </div>

                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full bg-[#0a1628] hover:bg-[#0a1628]/90 text-white font-semibold py-5 text-base uppercase tracking-wider rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Join the Team'
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div className="order-2 lg:order-1">
                <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">About the Candidate</span>
                <h2 className="text-3xl lg:text-4xl font-bold text-[#0a1628] mt-2 mb-6">
                  Meet Kalinda Washington
                </h2>
                <div className="w-16 h-1 bg-red-600 mb-6"></div>
                <p className="text-gray-600 leading-relaxed mb-5">
                  With over 15 years of experience in education leadership, Kalinda Washington brings real-world classroom experience, not Albany talking points, to the State Assembly race.
                </p>
                <p className="text-gray-600 leading-relaxed mb-8">
                  A proud mother, aunt, and community leader, Kalinda is committed to supporting school choice, backing our police, and fighting for affordability so working families can thrive across Monroe County.
                </p>
                <Link to="/meet-kalinda">
                  <Button className="bg-[#0a1628] hover:bg-[#0a1628]/90 text-white font-semibold px-8 py-5 text-base transition-all duration-300 group">
                    Learn More About Kalinda
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
              <div className="order-1 lg:order-2">
                <div className="relative">
                  <div className="absolute -inset-4 bg-red-600/10 rounded-2xl -z-10"></div>
                  <div className="rounded-xl shadow-xl overflow-hidden">
                    <img
                      src="https://customer-assets.emergentagent.com/job_f99a18a4-e930-4176-ac10-f2e206aadd3b/artifacts/1v87awdn_combined_2000px.jpeg"
                      alt="Kalinda Washington at community parade and graduation celebration"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Endorsements Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="max-w-5xl mx-auto text-center">
            <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Proudly Endorsed</span>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#0a1628] mt-2 mb-12">
              Trusted by Leaders Who Serve
            </h2>
            
            <div className="flex flex-wrap justify-center items-center gap-12 lg:gap-20">
              {/* Locust Club - Rochester Police */}
              <div className="flex flex-col items-center">
                <img
                  src="https://customer-assets-v7afamib.emergentagent.net/job_f99a18a4-e930-4176-ac10-f2e206aadd3b/artifacts/j98e7tcz_image.png"
                  alt="Locust Club - Rochester Police Est. 1904"
                  className="h-32 sm:h-40 w-auto object-contain"
                />
                <p className="mt-4 text-sm font-semibold text-[#0a1628]">Locust Club</p>
                <p className="text-xs text-gray-500">Rochester Police Union</p>
              </div>
              
              {/* NYSFRW */}
              <div className="flex flex-col items-center">
                <img
                  src="https://customer-assets-v7afamib.emergentagent.net/job_f99a18a4-e930-4176-ac10-f2e206aadd3b/artifacts/z8wualhl_image.png"
                  alt="New York State Federation of Republican Women"
                  className="h-32 sm:h-40 w-auto object-contain"
                />
                <p className="mt-4 text-sm font-semibold text-[#0a1628]">NYSFRW</p>
                <p className="text-xs text-gray-500">NY State Federation of Republican Women</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Campaign Quote */}
      <section className="py-16 bg-[#0a1628]">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <svg className="w-12 h-12 text-red-600/50 mx-auto mb-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
            </svg>
            <blockquote className="text-xl sm:text-2xl lg:text-3xl text-white font-light leading-relaxed mb-6 italic">
              "It's time for leaders who listen to parents, support our police, and fight for the working families of Monroe County."
            </blockquote>
            <cite className="text-white/70 font-medium">— Kalinda Washington</cite>
          </div>
        </div>
      </section>

      {/* Connect Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#0a1628] mb-4">
              Connect with Kalinda
            </h2>
            <p className="text-gray-600 text-lg mb-10">
              Follow the campaign on social media for the latest updates.
            </p>
            
            <div className="flex justify-center gap-6 mb-16">
              <a
                href="https://www.facebook.com/share/1E1zLz6hvX/?mibextid=wwXIfr"
                target="_blank"
                rel="noopener noreferrer"
                className="w-14 h-14 bg-[#0a1628] rounded-full flex items-center justify-center text-white hover:bg-red-600 transition-colors duration-300"
                aria-label="Facebook"
              >
                <Facebook className="w-6 h-6" />
              </a>
              <a
                href="https://www.instagram.com/kalindafornysa?igsh=MWFkOW9jaWE3OHFidw%3D%3D&utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                className="w-14 h-14 bg-[#0a1628] rounded-full flex items-center justify-center text-white hover:bg-red-600 transition-colors duration-300"
                aria-label="Instagram"
              >
                <Instagram className="w-6 h-6" />
              </a>
            </div>

            {/* Donate CTA */}
            <div className="bg-gradient-to-r from-[#0a1628] to-[#1a2d4a] rounded-2xl p-10 lg:p-12 text-white shadow-2xl">
              <h3 className="text-2xl lg:text-3xl font-bold mb-3">
                Support Our Campaign
              </h3>
              <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto">
                Your contribution powers our grassroots movement to bring common-sense leadership to Albany.
              </p>
              <a href="https://buy.stripe.com/4gMeVec2v4oP6XP3sQ6g803" target="_blank" rel="noopener noreferrer">
                <Button 
                  size="lg" 
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold px-10 py-6 text-base uppercase tracking-wider shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  Donate Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
